import { Datum } from "../../../components/docs/Datum.js";
import { note, p, table, ul } from "../blocks.js";
import { QUOTED } from "../thresholds.js";
import type { WikiTopic } from "../types.js";

export const confluence: WikiTopic = {
  slug: "confluence",
  title: "Confluence",
  summary: "Agreement scored across the hourly strategies that are switched on.",
  group: "strategies",
  horizon: "session",
  strategyKey: "confluence",
  sections: [
    {
      heading: "What it looks for",
      code: "confluence_bullish / _bullish_strong / _bearish / _bearish_strong",
      blocks: [
        p(
          <>
            Confluence is not a strategy in the registry. Nothing evaluates bars for it. It is
            synthesised from the votes the other strategies cast in the same bucket on the same
            symbol, counting distinct strategy ids per side:{" "}
            <Datum>{QUOTED.CONFLUENCE_FLOOR}</Datum> agreeing strategies is weak and{" "}
            <Datum>{QUOTED.CONFLUENCE_STRONG_FLOOR}</Datum> or more is strong.
          </>,
        ),
        p(
          <>
            It is the only filter in the design that improves the signal rather than shrinking the
            count. An hourly EMA cross alone fires on 2 to 4% of names per bucket, so six strategies
            land between 400 and 800 candidates a day and Telegram needs tens. Individual strategy
            rows are written at <Datum>info</Datum> and reach nobody; a weak agreement row is
            written at <Datum>warn</Datum> and a strong one at <Datum>urgent</Datum>.
          </>,
        ),
        p(
          <>
            Distinct strategies, not vote count. The runner returns at most one result per strategy
            today, so the distinction changes nothing now; it is what stops a multi-result strategy
            from silently overstating agreement later.
          </>,
        ),
      ],
    },
    {
      heading: "Why strength is a separate type",
      blocks: [
        p(
          <>
            Escalation could have been a raised severity on the existing row. It cannot be. Inserts
            are <Datum>ON CONFLICT DO NOTHING</Datum>, which is the whole cooldown mechanism: two
            strategies agree at 10:15 and write a weak row; four agree at 13:15 on the same symbol
            and session, and that row is dropped.
          </>,
        ),
        p(
          <>
            Updating the severity in place does not fix it either. The 10:15 row was already
            delivered, so a delivery record exists for it and the undelivered query excludes it; the
            raised severity would reach nobody. The escalation has to be a new row, and a new row
            needs a distinct signal type to survive the unique index.
          </>,
        ),
      ],
    },
    {
      heading: "Who may vote",
      blocks: [
        p(<>Two filters run before the count.</>),
        ul([
          <>
            A strategy marked <Datum>confluence: false</Datum> is dropped. Only Prophet swing today:
            a swing entry driven by a weekly setup is not independent intraday evidence, and since
            it ships at <Datum>info</Datum> while agreement rows deliver at <Datum>warn</Datum>,
            voting would route it to Telegram by accident. Its own rows are still written; the
            opt-out changes what agreement is scored from, not what is recorded.
          </>,
          <>
            Any strategy-and-type pair that already fired in the previous bucket of the same session
            is dropped. Confluence means two strategies agree now, and a strategy whose condition is
            a standing state re-asserts the same thing every bucket it holds. Freshness is decided
            by re-running the voting strategies against the previous bucket, because the runner is
            pure and a strategy's only history is its bars.
          </>,
        ]),
        table(
          "Confluence gates",
          ["Gate", "Value"],
          [
            ["Weak floor", <Datum key="w">{QUOTED.CONFLUENCE_FLOOR} strategies</Datum>],
            ["Strong floor", <Datum key="s">{QUOTED.CONFLUENCE_STRONG_FLOOR} strategies</Datum>],
            ["Agreement window", "One bucket, one symbol, one side"],
            ["Weak severity", <Datum key="v">warn</Datum>],
            ["Strong severity", <Datum key="u">urgent</Datum>],
          ],
        ),
      ],
    },
    {
      heading: "What the row carries",
      blocks: [
        p(
          <>
            The payload names the agreeing strategies in registry order, the vote count, the bucket,
            and the signal types that voted. Levels are copied from the agreeing votes: the nearest
            stop and the nearest target among them.
          </>,
        ),
        p(
          <>
            Nearest, not widest and not averaged. Nearest is the level price reaches first, and
            averaging would invent a number no strategy proposed. Every vote shares one bucket, so
            the entry price is the same in all of them. Without this the only intraday rows a reader
            could act on would be the ones nobody is sent: the individual rows carry entry, stop,
            and target, and the row saying three strategies agree carried a vote count and nothing
            else.
          </>,
        ),
        note(
          <>
            Confluence restates rows the feed already holds. On a bucket where three strategies
            agree, the feed carries <Datum>5</Datum> rows for <Datum>3</Datum> observations, so any
            count taken over the feed overstates by that ratio. The catalogue marks it{" "}
            <Datum>derived</Datum> for exactly this reason.
          </>,
        ),
      ],
    },
  ],
};
